import { zipWith } from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";

import { IResolveResponse } from "../api/ResolveApi/resolve-api.types";
import { IClassIdAction } from "../App/Classes/actions";
import { IClass } from "../App/types/common";
import { modifyObjectDeletedPatch, patchPayloadCreator } from "../components/course-author/helpers/patches/creators";
import { api } from "../dependencies";
import { hasErrorRecords, IClassResponse, resolveClassPostSuccessResponseValueToClassUIData } from "../parsers/resolve";
import { SagaAction } from "../sagas/types";
import * as selectors from "../selectors/selectors";
import { Creators } from "./common";
import { SagaFunction } from "./types";

export interface IFetchAllClassesFurtherActions {
  onSuccess: ActionFunction1<IClass[], Action<IClass[]>>;
  onFailure: ActionFunction1<
    {
      message: string;
    },
    Action<{
      message: string;
    }>
  >;
}

export function* fetchAndResolveClasses(accountId: string, organizationId: string) {
  const [classIdsError, classIdsResponse] = yield call(api.classes.getEditableByRelativeTo, accountId, organizationId);
  if (classIdsError) {
    return [{ message: "Failed to fetch classes." }, null];
  } else {
    const [classesError, classesResponse] = yield call(api.resolve.class.post, accountId, classIdsResponse.data);
    if (classesError) {
      return [{ message: "Failed to fetch classes." }, null];
    } else if (hasErrorRecords(classesResponse.data)) {
      return [{ message: "Classes contained errors." }, null];
    } else {
      return [null, { classIdsResponse, classesResponse }];
    }
  }
}

export const fetchAllClasses: (furtherActions: IFetchAllClassesFurtherActions) => SagaFunction = furtherActions =>
  function* _fetchAllClasses(action: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const organizationId = yield select(selectors.organizationId);
    const [classesError, { classIdsResponse, classesResponse }] = yield call(
      fetchAndResolveClasses,
      accountId,
      organizationId
    );
    if (classesError) {
      return yield put(furtherActions.onFailure(classesError));
    }
    const classes = zipWith(
      (uuid, classData) => ({
        ...resolveClassPostSuccessResponseValueToClassUIData(classData),
        classId: uuid,
      }),
      classIdsResponse.data as string[],
      classesResponse.data as Array<IResolveResponse<IClassResponse>>
    );
    yield put(furtherActions.onSuccess(classes));
  };

export interface IFetchClassFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const fetchClass: (furtherActions: IFetchClassFurtherActions) => SagaFunction = furtherActions =>
  function* _fetchClass(action: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const [classesError, classesResponse] = yield call(api.resolve.class.post, accountId, [action.classId]);
    if (classesError) {
      yield put(furtherActions.onFailure({ message: "Failed to fetch class by id." }));
    } else if (hasErrorRecords(classesResponse.data)) {
      yield put(furtherActions.onFailure({ message: "Class contained errors." }));
    } else {
      const data = zipWith(
        (uuid, datum) => ({
          ...resolveClassPostSuccessResponseValueToClassUIData(datum as IResolveResponse<IClassResponse>),
          classId: uuid,
        }),
        [action.classId],
        classesResponse.data
      );
      yield put(furtherActions.onSuccess(data[0]));
    }
  };

export interface IDeleteClassFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const deleteClass: (fa: IDeleteClassFurtherActions) => SagaFunction = fa =>
  function* _deleteClass(action: IClassIdAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const { classId } = action;
    const oldClasses: IClass[] = yield select(selectors.classes);
    const classesToDelete: IClass[] = oldClasses.filter(c => c.classId === classId);
    if (classesToDelete.length === 0) {
      return yield put(fa.onFailure({ message: "Class requested to delete not found." }));
    }
    const versionId = classesToDelete[0].versionId;
    const deletePatch = yield call(modifyObjectDeletedPatch, versionId, accountId);
    const patchPayload = yield call(patchPayloadCreator, versionId, deletePatch);
    const [patchError] = yield call(api.classes.patchClass, accountId, classId, patchPayload);
    if (patchError) {
      return yield put(
        fa.onFailure({
          message: "Failed to patch course as deleted.",
        })
      );
    }
    const classes = oldClasses.filter(c => c.classId !== classId);
    yield put(Creators.loadClassAmount());
    yield put(fa.onSuccess(classes));
  };
